<template>
  <mia-modal
    id="car-file-customer-invoice-modal"
    classes="v--modal flex flex-col"
    width="80%"
    height="auto"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <mia-modal-header>
      <h3 class="py-0 my-0">
        Kundenrechnung bereitstellen
      </h3>
    </mia-modal-header>

    <div class="p-4">
      <div
        v-if="isUploadingFile"
        class="flex justify-center py-12"
      >
        Datei wird hochgeladen...
      </div>
      <mia-form-section-grid v-else>
        <div
          v-if="!hasInvoiceDateSold"
          ref="invoicedateSoldWrapperRef"
        >
          <mia-feature-input
            v-model="form.invoicedate_sold"
            label="VK-Rechnungsdatum"
            :errors="getErrors('invoicedate_sold')"
            type="date"
            required
            @input="clearErrors('invoicedate_sold')"
          />
        </div>

        <div class="col-span-2">
          <span class="font-bold mr-2 tracking-wider text-gray-800">
            Offene Forderungen und Zahlungen
          </span>

          <datatable
            v-if="hasDebts"
            class="w-full table-auto datatable mt-2"
            :data="debtsCombined"
            :columns="debtColumns"
          >
            <template #footer>
              <tr>
                <td
                  colspan="4"
                  class="text-right"
                >
                  Summe Forderungen
                </td>
                <td class="text-right">
                  {{ debtStats.sum | price('EUR') }}
                </td>
              </tr>
              <tr>
                <td
                  colspan="4"
                  class="text-right"
                >
                  Summe Zahlungen
                </td>
                <td class="text-right">
                  {{ debtStats.sumPaid | price('EUR') }}
                </td>
              </tr>
              <tr>
                <td
                  colspan="4"
                  class="text-right"
                >
                  Differenz
                </td>
                <td
                  class="font-bold text-right"
                  :class="{
                    'text-red-700': (debtStats.sum - debtStats.sumPaid) > 0,
                    'text-blue-700': ((debtStats.sum - debtStats.sumPaid) < 0)
                  }"
                >
                  {{ debtStats.difference | price('EUR') }}
                </td>
              </tr>
            </template>
          </datatable>
          <mia-notice
            v-else
            permanent
            class="mt-2"
          >
            Für dieses Fahrzeug wurden noch keine Forderungen angelegt
          </mia-notice>
        </div>

        <div class="col-span-2">
          <div class="text-gray-800 font-bold">
            OPOS Forderung eintragen <span class="italic font-normal">(optional)</span>
          </div>
          <mia-debt-form
            class="mt-4"
            :debtor.sync="form.debtor"
            :invoice-id.sync="form.invoice_id"
            :date.sync="form.date"
            :title.sync="form.title"
            :value.sync="form.value"
            :errors-key="backendValidationErrorsKey"
          />
        </div>
      </mia-form-section-grid>
    </div>

    <mia-modal-footer class="flex items-center justify-end">
      <mia-cancel-button
        label="Abbrechen"
        type="button"
        :disabled="$wait.any || isUploadingFile"
        @click="$emit('cancel')"
      />

      <mia-submit-button
        label="Kundenrechnung bereitstellen"
        type="button"
        class="ml-3"
        :disabled="$wait.any || !canSubmit || isUploadingFile"
        @click="onSubmit"
      />
    </mia-modal-footer>
  </mia-modal>
</template>

<script>
import { handleEmptyDate, isEmpty, tryScrollingIntoView } from '@/helpers/utils'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import { addDebt, getDebts } from '@/api/cars'
import DateTimeColumn from '@/components/ui/datatable/columns/DateTimeColumn.vue'
import PriceColumn from '@/components/ui/datatable/columns/PriceColumn.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import DebtForm from '@/components/cars/debts/DebtForm.vue'

export default {
  name: 'CarFileCustomerInvoiceModal',

  components: {
    'mia-cancel-button': CancelButton,
    'mia-debt-form': DebtForm,
    'mia-feature-input': FeatureInput,
    'mia-form-section-grid': FormSectionGrid,
    'mia-notice': Notice,
    'mia-submit-button': SubmitButton
  },

  mixins: [HandlesBackendValidationErrors],

  props: {
    isUploadingFile: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      form: {
        invoicedate_sold: '',
        debtor: 'customer',
        invoice_id: '',
        date: '',
        title: '',
        value: ''
      },
      debts: [],
      debtsPaid: [],
      debtStats: {
        difference: 0,
        sum: 0,
        sumPaid: 0
      },
      debtColumns: [
        {
          field: 'item_date',
          label: 'Datum',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            displayFormat: 'dd.MM.yyyy'
          }
        },
        {
          field: '',
          label: 'Typ',
          headerAlign: 'left',
          sortable: false,
          representedAs: row => (this.debts.includes(row) ? 'Forderung' : 'Zahlung')
        },
        {
          field: 'item_invoice_id',
          label: 'Rechnungsnummer',
          headerAlign: 'left'
        },
        {
          field: 'item_debtor_title',
          label: 'Debitor',
          representedAs: row => row.item_debtor_title || 'Kunde'
        },
        {
          field: 'item_value',
          label: 'Bruttosumme',
          align: 'right',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR',
            class: row => (this.debts.includes(row) ? 'text-red-700' : '')
          }
        }
      ]
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    hasInvoiceDateSold() {
      return !isEmpty(handleEmptyDate(this.car.item_invoicedate_sold))
    },

    canSubmit() {
      if (this.hasInvoiceDateSold) {
        return true
      }

      return this.form.invoicedate_sold !== ''
    },

    hasDebts() {
      return this.debts.length > 0 || this.debtsPaid.length > 0
    },

    debtsCombined() {
      return this.debts.concat(this.debtsPaid)
    }
  },

  created() {
    this.loadDebts()
  },

  methods: {
    async loadDebts() {
      const [errors, data] = await getDebts(this.car.id)

      if (errors) {
        console.error(errors)
        window.eventBus.$emit('mia-error', {
          error: ['Forderungen des Fahrzeugs konnten nicht geladen werden.']
        })
        return
      }

      this.debts = data.debts
      this.debtsPaid = data.debtsPaid
      this.debtStats = data.stats
    },

    async onSubmit() {
      const payload = {
        ...this.form
      }

      if (payload.debtor === 'customer' && isEmpty(payload.invoice_id) && isEmpty(payload.date) && isEmpty(payload.title) && isEmpty(payload.value)) {
        payload.debtor = ''
      }

      if (!this.hasInvoiceDateSold || Object.values(payload).some(value => value !== '')) {
        this.$wait.start('adding debt')
        const [errors] = await addDebt(this.car.id, payload)
        this.$wait.end('adding debt')

        if (errors) {
          this.handleBackendValidationErrors(errors)
          window.eventBus.$emit('mia-error', {
            error: ['Daten konnten nicht gespeichert werden.']
          })

          this.$nextTick(() => {
            if (this.hasErrors('invoicedate_sold')) {
              tryScrollingIntoView(this.$refs.invoicedateSoldWrapperRef)
            }
          })

          return
        }
      }

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich gespeichert.']
      })

      this.$emit('saved')
    }
  }
}
</script>
